// src/utils/dashboardPeriodMemory.ts
//
// The dashboards' period choice, remembered per user + account in browser
// storage (ADR-0215). Plan: docs/plans-pending/2026-09-03-dashboard-period-
// remembered-per-user.md.
//
// Two documents per user + account (plan 2026-10-11 dashboard period scope,
// step A1, amending ADR-0215 decision 1):
// - the OVERVIEW period (`dashboardPeriodStorageKey`), unchanged — also the
//   fallback for a dashboard that has no period of its own;
// - a map `dashboardPublicId → period` (`dashboardPeriodByDashboardKey`), one
//   entry per dashboard the user picked a period on, capped at the
//   `MAX_REMEMBERED_DASHBOARDS` most recently written.
// A dashboard opens on its own period, else the overview's, else the platform
// default (`resolveDashboardPeriod`).
//
// What is stored is the CHOICE, never the resolved dates of a relative
// preset: "this_year" written in January must still mean the current year
// when it is read in September, so a relative preset is recomputed through
// `getPresetDates` on restore. Only a `custom` window carries dates.
//
// Everything read back is untrusted — localStorage is writable by the user
// and by any script on the origin — and the page formats the restored dates
// on every render (`format()` throws on an Invalid Date), so every field is
// validated and anything that cannot be reconstructed restores as `null`,
// which lands the page on the platform default exactly as before.

import {
  differenceInCalendarDays,
  endOfDay,
  format,
  isValid,
  parseISO,
  startOfDay,
} from "date-fns";

import type {
  ComparisonMode,
  NamedPreset,
  TimeRange,
  TimeRangePreset,
} from "./comparisonWindow";
import { getDefaultTimeRange, getPresetDates, parseRollingPreset } from "./comparisonWindow";

/**
 * Longest window a stored custom range may span, in days between its start
 * and end. Counterpart of the backend's `MAX_REFRESH_RANGE_DAYS`
 * (`apps/backend/api/schemas/dashboard_schemas.py`), which answers 422 on
 * every tile request above it — a wider stored window would break the page
 * on every visit instead of until the next refresh.
 */
export const MAX_STORED_RANGE_DAYS = 366 * 3;

const STORAGE_KEY_PREFIX = "verity:dashboards:period:v1:";
const BY_DASHBOARD_KEY_PREFIX = "verity:dashboards:period-by-dashboard:v1:";

/** How many dashboards keep a period of their own; the least recently
 *  written entry is dropped first. Bounds the document and the number of
 *  dashboard ids left in a shared browser (the exposure ADR-0215 accepted for
 *  one key, now up to this many — recorded in the plan's ADR). */
export const MAX_REMEMBERED_DASHBOARDS = 50;

/** A dashboard public id, exactly. The map's keys come from the URL and from
 *  storage — both untrusted — so anything else (`__proto__`, `constructor`,
 *  a typo) is refused rather than indexed. */
const DASHBOARD_PUBLIC_ID = /^dash_[0-9a-f]{32}$/;

// `satisfies Record<Union, true>`: a preset added to the union without being
// added here is a type error, not a value that is silently rejected on read.
// Over `NamedPreset`, not `TimeRangePreset`: the rolling spelling is a family
// of infinitely many strings and is validated by its PARSER instead (which
// enforces the same bounds the backend does) — see `isPreset`.
const TIME_RANGE_PRESETS = {
  today: true,
  yesterday: true,
  last_7_days: true,
  last_14_days: true,
  last_28_days: true,
  last_30_days: true,
  last_90_days: true,
  last_365_days: true,
  this_week: true,
  last_week: true,
  this_month: true,
  last_month: true,
  this_quarter: true,
  last_quarter: true,
  this_year: true,
  last_year: true,
  custom: true,
} satisfies Record<NamedPreset, true>;

const COMPARISON_MODES = {
  none: true,
  previous_period: true,
  previous_year: true,
  custom: true,
} satisfies Record<ComparisonMode, true>;

/** The document as written to storage. Dates are `yyyy-MM-dd`. */
export type StoredDashboardPeriod = {
  preset: TimeRangePreset;
  comparisonMode: ComparisonMode;
  /** Only when `preset === "custom"`. */
  startDate?: string;
  endDate?: string;
  /** Only when `comparisonMode === "custom"` and both dates were set. */
  comparisonStartDate?: string;
  comparisonEndDate?: string;
  /** "Also compare against the target" (W1.8). Written only when on and only
   *  under a mode that can carry it — see `carriesTarget`. Absent in every
   *  document written before plan 2026-09-14-the-plan-leg-on-every-surface
   *  (S1), which restores exactly as it did. */
  withTarget?: true;
  /** "KPI cards also show the other period" (plan 2026-09-14-the-plan-leg-on-
   *  every-surface, S4/S5). Only under `previous_period` / `previous_year` —
   *  see `carriesOtherPeriod`. */
  withOtherPeriod?: true;
};

/** The modes that have an OTHER shifted period: each is the other's. */
function carriesOtherPeriod(mode: ComparisonMode): boolean {
  return mode === "previous_period" || mode === "previous_year";
}

/**
 * The modes the plan can sit BESIDE: a window. The picker offers the switch
 * under the same three (`TimeRangeSelector`'s `windowMode`); under `none`
 * there is nothing to sit beside.
 */
function carriesTarget(mode: ComparisonMode): boolean {
  return mode === "previous_period" || mode === "previous_year" || mode === "custom";
}

/**
 * The storage key for one user in one account, or `null` when either id is
 * missing. A key built from `undefined` would be one bucket shared by every
 * viewer on the machine; the null makes that impossible by construction.
 */
export function dashboardPeriodStorageKey(
  userId: string | null | undefined,
  accountId: string | null | undefined,
): string | null {
  if (!userId || !accountId) return null;
  return `${STORAGE_KEY_PREFIX}${userId}:${accountId}`;
}

/** The per-dashboard map's key for one user in one account; `null` when
 *  either id is missing, for the same reason as the overview key. */
export function dashboardPeriodByDashboardKey(
  userId: string | null | undefined,
  accountId: string | null | undefined,
): string | null {
  if (!userId || !accountId) return null;
  return `${BY_DASHBOARD_KEY_PREFIX}${userId}:${accountId}`;
}

const DAY = "yyyy-MM-dd";

export function serialiseDashboardPeriod(range: TimeRange): StoredDashboardPeriod {
  const doc: StoredDashboardPeriod = {
    preset: range.preset,
    comparisonMode: range.comparisonMode,
  };
  if (range.preset === "custom") {
    doc.startDate = format(range.startDate, DAY);
    doc.endDate = format(range.endDate, DAY);
  }
  if (
    range.comparisonMode === "custom"
    && range.comparisonStartDate
    && range.comparisonEndDate
  ) {
    doc.comparisonStartDate = format(range.comparisonStartDate, DAY);
    doc.comparisonEndDate = format(range.comparisonEndDate, DAY);
  }
  if (range.withTarget === true && carriesTarget(range.comparisonMode)) {
    doc.withTarget = true;
  }
  if (range.withOtherPeriod === true && carriesOtherPeriod(range.comparisonMode)) {
    doc.withOtherPeriod = true;
  }
  return doc;
}

// `Object.hasOwn`, not `in`: a stored preset of "constructor" or "toString"
// is `in` every object literal.
//
// A rolling spelling is accepted through the parser, which refuses one out of
// range (`rolling_400_days`) exactly as the backend does — so a document
// written by hand cannot restore a window the backend will 422, and the page
// lands on the platform default instead.
function isPreset(value: unknown): value is TimeRangePreset {
  if (typeof value !== "string") return false;
  return Object.hasOwn(TIME_RANGE_PRESETS, value) || parseRollingPreset(value) !== null;
}

function isComparisonMode(value: unknown): value is ComparisonMode {
  return typeof value === "string" && Object.hasOwn(COMPARISON_MODES, value);
}

/**
 * `yyyy-MM-dd` → a valid Date, or null. `parseISO` returns an Invalid Date
 * rather than throwing, so the shape check and `isValid` are both needed:
 * "2026-02-30" passes the regex and fails `isValid`.
 */
function parseStoredDay(value: unknown): Date | null {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
}

/**
 * A window the rest of the stack accepts: ordered, no wider than the backend
 * allows, and not reaching past today (the picker's calendar has a `maxDate`
 * of today and no `minDate`, so these are the two bounds it does not enforce
 * for a document written by hand).
 */
function parseStoredWindow(
  startRaw: unknown,
  endRaw: unknown,
  now: Date,
): { start: Date; end: Date } | null {
  const startDay = parseStoredDay(startRaw);
  const endDay = parseStoredDay(endRaw);
  if (!startDay || !endDay) return null;
  const start = startOfDay(startDay);
  const end = endOfDay(endDay);
  if (start > end) return null;
  if (differenceInCalendarDays(end, start) > MAX_STORED_RANGE_DAYS) return null;
  if (end > endOfDay(now)) return null;
  return { start, end };
}

/**
 * The stored document back into a `TimeRange`, or `null` when it cannot be
 * reconstructed (junk JSON, unknown preset or comparison mode, a `custom`
 * preset without a valid window). A relative preset is recomputed for today.
 *
 * A custom COMPARISON window that fails validation downgrades the comparison
 * to `"none"` rather than discarding the document: "custom" with no dates is
 * a state the picker lets a user apply today, and throwing the main period
 * away over it would be worse than the bug being avoided.
 */
export function restoreDashboardPeriod(raw: string | null | undefined): TimeRange | null {
  if (!raw) return null;
  let doc: unknown;
  try {
    doc = JSON.parse(raw);
  } catch {
    return null;
  }
  return restoreDashboardPeriodDocument(doc);
}

/** `restoreDashboardPeriod` over an already-parsed value: the per-dashboard
 *  map validates every entry with exactly the rules of the overview key, and
 *  a date range control's URL value with the same rules (controls A4b). */
export function restoreDashboardPeriodDocument(doc: unknown): TimeRange | null {
  if (typeof doc !== "object" || doc === null || Array.isArray(doc)) return null;
  let record = doc as Record<string, unknown>;
  // A remembered Target choice (plan 2026-09-15-the-target-is-an-extra-
  // comparison-not-a-mode). Production offered Target as a comparison mode;
  // the picker no longer does. Translated FIRST — before the mode is validated
  // (an unknown mode throws the whole document away) and before the custom
  // downgrade below — into the default comparison with the plan beside it.
  // Only the mode and the switch: the old mode meant "show the target", so the
  // switch is forced on, and a hand-edited `withOtherPeriod` beside it must
  // not turn on. Runs on every restore: a tab still on the previous bundle may
  // write "target" back.
  if (record.comparisonMode === "target") {
    const { withOtherPeriod: _dropped, ...rest } = record;
    record = { ...rest, comparisonMode: getDefaultTimeRange().comparisonMode, withTarget: true };
  }
  if (!isPreset(record.preset) || !isComparisonMode(record.comparisonMode)) return null;

  const now = new Date();
  let startDate: Date;
  let endDate: Date;
  if (record.preset === "custom") {
    const window = parseStoredWindow(record.startDate, record.endDate, now);
    if (!window) return null;
    startDate = window.start;
    endDate = window.end;
  } else {
    ({ startDate, endDate } = getPresetDates(record.preset));
  }

  const range: TimeRange = {
    preset: record.preset,
    startDate,
    endDate,
    comparisonMode: record.comparisonMode,
  };
  if (record.comparisonMode === "custom") {
    const window = parseStoredWindow(record.comparisonStartDate, record.comparisonEndDate, now);
    if (window) {
      range.comparisonStartDate = window.start;
      range.comparisonEndDate = window.end;
    } else {
      range.comparisonMode = "none";
    }
  }
  // After the downgrade above, so a custom comparison that came back as
  // `none` does not carry the plan beside nothing. Exactly `true`: a document
  // edited by hand to `"false"` must not read as on.
  if (record.withTarget === true && carriesTarget(range.comparisonMode)) {
    range.withTarget = true;
  }
  if (record.withOtherPeriod === true && carriesOtherPeriod(range.comparisonMode)) {
    range.withOtherPeriod = true;
  }
  return range;
}

// The try/catch wraps the `localStorage` ACCESSOR, not only getItem/setItem:
// with site data blocked, Safari throws on `window.localStorage` itself, and
// the read runs inside a lazy useState initialiser with no boundary above it.

export function readStoredDashboardPeriod(key: string | null): TimeRange | null {
  if (!key) return null;
  try {
    return restoreDashboardPeriod(window.localStorage.getItem(key));
  } catch {
    return null;
  }
}

export function writeStoredDashboardPeriod(key: string | null, range: TimeRange): void {
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(serialiseDashboardPeriod(range)));
  } catch {
    // Private mode / quota / blocked storage — the picker itself must keep working.
  }
}


// ── per-dashboard periods (plan 2026-10-11 dashboard period scope, A1) ──────

/** One validated entry of the per-dashboard map. `at` is when it was written
 *  (epoch ms), the LRU order. */
type DashboardPeriodEntry = { period: StoredDashboardPeriod; at: number };

/**
 * The map's entries that survive validation, newest first, at most
 * `MAX_REMEMBERED_DASHBOARDS`. Re-capped on READ as well as on write: a
 * document written by hand (or by an older/newer tab) must not grow the page's
 * work without bound. An entry is dropped when its id is not a dashboard public
 * id, its timestamp is not a finite past number, or its period does not
 * restore — never the whole map for one bad entry.
 */
function readEntries(raw: string | null): Map<string, DashboardPeriodEntry> {
  const out = new Map<string, DashboardPeriodEntry>();
  if (!raw) return out;
  let doc: unknown;
  try {
    doc = JSON.parse(raw);
  } catch {
    return out;
  }
  if (typeof doc !== "object" || doc === null || Array.isArray(doc)) return out;
  const entries = (doc as Record<string, unknown>).entries;
  if (typeof entries !== "object" || entries === null || Array.isArray(entries)) return out;
  const now = Date.now();
  const valid: Array<[string, DashboardPeriodEntry]> = [];
  for (const id of Object.keys(entries)) {
    if (!DASHBOARD_PUBLIC_ID.test(id) || !Object.hasOwn(entries, id)) continue;
    const entry = (entries as Record<string, unknown>)[id];
    if (typeof entry !== "object" || entry === null || Array.isArray(entry)) continue;
    const { period, at } = entry as Record<string, unknown>;
    if (typeof at !== "number" || !Number.isFinite(at) || at > now) continue;
    const range = restoreDashboardPeriodDocument(period);
    if (!range) continue;
    valid.push([id, { period: serialiseDashboardPeriod(range), at }]);
  }
  valid.sort((a, b) => b[1].at - a[1].at);
  for (const [id, entry] of valid.slice(0, MAX_REMEMBERED_DASHBOARDS)) out.set(id, entry);
  return out;
}

function writeEntries(key: string, entries: Map<string, DashboardPeriodEntry>): void {
  // A prototype-free record: ids are validated, but the written document is
  // read by other tabs and future bundles that may index it naively.
  const record: Record<string, DashboardPeriodEntry> = Object.create(null);
  for (const [id, entry] of entries) record[id] = entry;
  window.localStorage.setItem(key, JSON.stringify({ entries: record }));
}

/** Where a dashboard's period came from — the picker's footer says which.
 *  `dashboard_default`: the period the dashboard's author stored on it
 *  (ADR-0496 D3). */
export type DashboardPeriodSource = "dashboard" | "dashboard_default" | "overview" | "default";

/**
 * The period a dashboard opens on: the viewer's own remembered period for it,
 * else the dashboard's stored default (`dashboardDefault`, ADR-0496 D3), else
 * the overview's, else `null` (the caller applies the platform default, as
 * before). Never throws: blocked storage, junk JSON or a bad id all fall through.
 */
export function resolveDashboardPeriod(
  byDashboardKey: string | null,
  overviewKey: string | null,
  dashboardId: string | null | undefined,
  dashboardDefault: TimeRange | null = null,
): { range: TimeRange | null; source: DashboardPeriodSource } {
  if (byDashboardKey && dashboardId && DASHBOARD_PUBLIC_ID.test(dashboardId)) {
    try {
      const entry = readEntries(window.localStorage.getItem(byDashboardKey)).get(dashboardId);
      const range = entry ? restoreDashboardPeriodDocument(entry.period) : null;
      if (range) return { range, source: "dashboard" };
    } catch {
      // Blocked storage: fall through to the overview, as a fresh browser would.
    }
  }
  if (dashboardDefault) return { range: dashboardDefault, source: "dashboard_default" };
  const overview = readStoredDashboardPeriod(overviewKey);
  return overview ? { range: overview, source: "overview" } : { range: null, source: "default" };
}

/**
 * Remember `range` as THIS dashboard's period. Re-reads storage first, so a
 * write in another tab since this page loaded is kept (the map is never written
 * back from memory), then drops the least recently written entries over the cap.
 */
export function writeDashboardPeriodFor(
  byDashboardKey: string | null,
  dashboardId: string | null | undefined,
  range: TimeRange,
): void {
  if (!byDashboardKey || !dashboardId || !DASHBOARD_PUBLIC_ID.test(dashboardId)) return;
  try {
    const entries = readEntries(window.localStorage.getItem(byDashboardKey));
    entries.delete(dashboardId);
    const next = new Map<string, DashboardPeriodEntry>([
      [dashboardId, { period: serialiseDashboardPeriod(range), at: Date.now() }],
      ...[...entries].slice(0, MAX_REMEMBERED_DASHBOARDS - 1),
    ]);
    writeEntries(byDashboardKey, next);
  } catch {
    // Private mode / quota / blocked storage — the picker itself must keep working.
  }
}

/** Forget this dashboard's own period: it follows the overview again. */
export function clearDashboardPeriodFor(
  byDashboardKey: string | null,
  dashboardId: string | null | undefined,
): void {
  if (!byDashboardKey || !dashboardId || !DASHBOARD_PUBLIC_ID.test(dashboardId)) return;
  try {
    const entries = readEntries(window.localStorage.getItem(byDashboardKey));
    if (!entries.delete(dashboardId)) return;
    writeEntries(byDashboardKey, entries);
  } catch {
    // Blocked storage: nothing was remembered either.
  }
}
